import { useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next/pages";

// custom
import { useRoleCreateMutation, useRoleUpdateMutation, ValidationErr } from "graphql/generated";
import { useLoading } from "lib/loadingContext";
import { useMe } from "lib/auth";

import OkMsgs from "components/common/OkMsgs";
import ErrMsgs, { useErrMsgs } from "components/common/ErrMsgs";
import { PermittableButton } from "components/common/PermissionComponents";
import NitoelPaper from "components/atoms/NitoelPaper";
import InputLabel from "components/atoms/InputLabel";
import { NitoelTable, NitoelTHead, NitoelTBody, NitoelTRow, NitoelTHeaderCell } from "components/atoms/NitoelTable";
import FormRow from "./form/FormRow";

// mui
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

type RoleFormProps = {
  currentName: string;
  currentPermissions: any;
  currentIsForExternalApprover?: boolean;
  id: string | null;
  isUsePurchaseRequest: boolean;
};

export type ResourceType = {
  key: string;
  name: string;
  nameKey?: string;
  others: {
    label: string;
    value: string;
  }[];
  permissions: ("read" | "write" | "export")[]; // 該当keyで、どの権限があるかを定義する
};

// ここに追加する場合は、必ずrailsのRole.full_accessの対応 + role.jsonのhelp追加も行うこと
export const RESOURCES: ResourceType[] = [
  {
    key: "quotation",
    name: "見積",
    others: [
      { label: "見積申請", value: "quotation_apply" },
      { label: "見積進行", value: "quotation_proceed" },
    ],
    permissions: ["read", "write", "export"],
  },
  {
    key: "quotation_config",
    name: "見積設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "quotation_comparison_table",
    name: "比較表一覧",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "quotation_comparison_application",
    name: "選定申請一覧",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "quotation_db",
    name: "見積一覧（見積DB）",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "quotation_smart_drive",
    name: "見積スマートドライブ",
    others: [],
    permissions: ["read"],
  },
  {
    key: "quotation_task",
    name: "見積タスク",
    others: [],
    permissions: ["read"],
  },
  {
    key: "project",
    name: "プロジェクト",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "project_config",
    name: "プロジェクト設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "cost_accumulation",
    name: "原価明細積上表",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "cost_impact_management",
    name: "原価影響管理",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "cost_impact_management_config",
    name: "原価影響設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "pre_quotation_config",
    name: "前見積設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "supplier_v2",
    name: "サプライヤ",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_user_info",
    name: "連絡先",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_equipment_info",
    name: "設備",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_outsourcing_info",
    name: "2次サプライヤ",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_survey_info",
    name: "取引先調査情報",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_financial_analysis",
    name: "財務分析",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "supplier_file_info",
    name: "サプライヤファイル情報",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_evaluation",
    name: "サプライヤ評価",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "supplier_site",
    name: "サプライヤ拠点情報",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_account_invitation_by_tenant_frontend",
    name: "招待管理",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "supplier_firm_info_confirmation_request",
    name: "仕入先調査管理",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "supplier_config",
    name: "サプライヤ設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "supplier_evaluation_config",
    name: "評価テンプレート設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "mold",
    name: "金型",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "mold_storage_application",
    name: "金型保管費申請一覧",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "mold_storage_payment",
    name: "金型保管費履歴",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "mold_disposal_application",
    name: "金型廃棄申請一覧",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "part",
    name: "品目",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "part_config",
    name: "品目設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "attachment_offer",
    name: "出図管理",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "product",
    name: "製品",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "product_config",
    name: "製品設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "search_similar_drawing",
    name: "類似図面検索",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "drawings",
    name: "図面管理(new)",
    others: [],
    permissions: ["read", "write"],
  },
  // TODO: デモ版なので画面から権限設定できないようにしておく
  // {
  //   key: "search_similar_drawing_v2",
  //   name: "図面管理",
  //   others: [],
  //   permissions: ["read"],
  // },
  {
    key: "workflow_acknowledgement",
    name: "ワークフロー承認",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "workflow",
    name: "ワークフロー設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "price_adjustment_preparing",
    name: "価格改定準備",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "price_adjustment",
    name: "価格改定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "price_adjustment_setting",
    name: "価格改定設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "price_adjustment_exclude_conditions",
    name: "価格改定除外設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "part_order_unit_price",
    name: "単価管理",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "price_registration_select_part",
    name: "単価登録開始（品目選択）",
    others: [],
    permissions: ["read"],
  },
  {
    key: "price_registration_source",
    name: "単価登録準備",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "price_registration",
    name: "単価登録申請",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "price_registration_part_supply_price",
    name: "単価登録支給単価",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "price_registration_price_expiration",
    name: "単価登録単価失効",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "price_registration_config",
    name: "単価登録申請設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "analytics_categories",
    name: "カテゴリ分析",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "analytics_part_compatible",
    name: "互換品目分析",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "part_transaction_analysis",
    name: "購買実績分析",
    others: [],
    permissions: ["read"],
  },
  {
    key: "cost_analysis",
    name: "コスト分析",
    others: [],
    permissions: ["read"],
  },
  {
    key: "cost_reduction_item",
    name: "原価低減管理",
    others: [{ label: "原価低減管理者", value: "cost_reduction_admin" }],
    permissions: ["read", "write", "export"],
  },
  {
    key: "admin_user",
    name: "管理者",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "employee",
    name: "社員",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "role",
    name: "ロール",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "department",
    name: "部署",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "position",
    name: "役職",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "csv_import",
    name: "インポート",
    others: [],
    permissions: ["read", "write", "export"],
  },
  {
    key: "csv_export",
    name: "エクスポート",
    others: [],
    permissions: ["read", "export"],
  },
  {
    key: "other_config",
    name: "その他設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "audit_logs",
    name: "監査ログ",
    others: [],
    permissions: ["read", "export"],
  },
  {
    key: "announcement",
    name: "お知らせ",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "enquete",
    name: "アンケート",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "supplier_contact",
    name: "サプライヤ連絡",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "task_settings",
    name: "タスク設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "task_board",
    name: "タスクボード",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "tenant_configs",
    name: "テナント全体設定",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "target_cost",
    name: "目標原価",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "data_share",
    name: "データ共有",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "group_data_share",
    name: "グループデータ共有",
    others: [],
    permissions: ["read", "write"],
  },
  {
    // BCP は閲覧と書き込みを分けず、write を持つ人だけが使う（NITOEL-12585）。read の項目は出さない
    // （read は write と同じ値で保存する。togglePermission 参照）
    key: "bcp",
    name: "BCP",
    others: [],
    permissions: ["write"],
  },
];

export const PURCHASE_REQUEST_RESOURCES: ResourceType[] = [
  {
    // 購入依頼そのものの権限。Role.full_access は tenant.after_create でしか適用されないので、
    // 既存テナントはここから付けるしか手段が無い（無いと専用メニューも出ず、V2 の API も 403）
    key: "purchase_request",
    name: "",
    nameKey: "purchase_request:resource.purchase_requests",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "purchase_request_catalog",
    name: "",
    nameKey: "purchase_request:resource.purchase_request_catalog",
    others: [],
    permissions: ["read", "write"],
  },
  {
    // 採番定義の登録・編集・繰り上げ・履歴参照。採番そのもの（業務画面からの発番）はこの権限を要しない
    key: "numbering_config",
    name: "",
    nameKey: "purchase_request:resource.numbering_config",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "department_routing",
    name: "",
    nameKey: "purchase_request:resource.department_routing",
    others: [],
    permissions: ["read", "write"],
  },
  {
    key: "purchase_receipt",
    name: "",
    nameKey: "purchase_receipt:resource.purchase_receipt",
    others: [],
    permissions: ["read", "write"],
  },
];

// 段階公開のあいだ、ロール編集画面から隠す。root ユーザーだけが ON にできる。
// **外す条件を切替前に書く**（見積タスクは H-2）。書かないと段階公開が恒久化する。
// グループデータ共有は正式リリース（全テナントへの提供開始）時に外す。
// 図面管理(new) は正式リリース（全テナントへの提供開始）時に外す。
// コスト分析は「S0〜S8 が dev で通り、PdM が顧客テナントへの開放を決めたとき」に外す（docs/cost_analysis/実装計画.md）。
// 外すときは、メニュー・ページ・v2 の API の root 限定（lib/costInsightAccess・@RequiresRoot）も一緒に外す。
const ROOT_ONLY_RESOURCE_KEYS = ["quotation_db", "quotation_task", "group_data_share", "drawings", "cost_analysis"];

const RoleForm: React.FC<RoleFormProps> = (props) => {
  const router = useRouter();
  const { t } = useTranslation(["common", "role"]);
  const { loadingWrapper } = useLoading();
  const me = useMe();

  const { currentName, currentPermissions, currentIsForExternalApprover, id, isUsePurchaseRequest } = props;

  const [name, setName] = useState(currentName);
  const [isForExternalApprover, setIsForExternalApprover] = useState(currentIsForExternalApprover ?? false);

  // 単価管理機能はユタカ様のみ表示する、WFの初期設定もOFFのため他ユーザーは使えない
  const tenantCode = me?.tenant?.code || "";
  const isYutakagTenant = tenantCode.startsWith("yutakag");
  const showExternalApproverCheckbox = me?.tenant?.hasParentRelation || currentIsForExternalApprover;
  const filteredResources = RESOURCES.filter((r) => {
    // part_order_unit_priceはyutakag始まりのテナントのみ表示
    if (r.key === "part_order_unit_price") {
      return isYutakagTenant;
    }
    if (ROOT_ONLY_RESOURCE_KEYS.includes(r.key)) {
      return me?.isRoot;
    }
    return true;
  }).concat(isUsePurchaseRequest ? PURCHASE_REQUEST_RESOURCES : []);

  const updatePermissions = () => {
    const next: Record<string, Record<string, unknown>> = Object.fromEntries(
      Object.entries(currentPermissions).map(([key, value]) => [key, { ...(value as Record<string, unknown>) }]),
    );
    // currentPermissions にない場合、filteredResourcesから追加する
    // otherは特殊なケースなので未対応
    filteredResources.forEach((r) => {
      if (!next[r.key]) {
        next[r.key] = {};
      }
      r.permissions.forEach((p) => {
        const bucket = next[r.key] as Record<string, unknown>;
        if (bucket[p] == null) {
          bucket[p] = 0;
        }
      });
    });
    return next as typeof currentPermissions;
  };

  const [permissions, setPermissions] = useState(updatePermissions);
  const [ok, setOk] = useState(false);
  const { errs, ng, setNg, clearAndCloseErrors, setAndOpenErrors } = useErrMsgs<ValidationErr>();

  const [, createRole] = useRoleCreateMutation();
  const [, updateRole] = useRoleUpdateMutation();

  const togglePermission = (resource_key: string, permission_key: string) => {
    const current = { ...permissions[resource_key] };
    const toggledValue = current[permission_key] === 1 ? 0 : 1;
    current[permission_key] = toggledValue;
    // read以外をつける時、readもつける。readないと基本その操作はできないから。
    if (permission_key !== "read" && toggledValue === 1) {
      current["read"] = toggledValue;
    }
    // read の項目を出さない resource（BCP）は、write を外したら read も外す（画面に見えない read だけが残らないように）
    const resource = filteredResources.find((r) => r.key === resource_key);
    if (permission_key === "write" && toggledValue === 0 && resource && !resource.permissions.includes("read")) {
      current["read"] = 0;
    }
    // 上記とは逆でreadを外す時、read以外も外す。
    if (permission_key === "read" && toggledValue === 0) {
      // write権限がないresourceもありうるので。
      Object.keys(current).forEach((permission) => {
        switch (permission) {
          case "read":
            break;
          case "other":
            current[permission] = { ...current[permission] };
            Object.keys(current[permission]).forEach((other_permission) => {
              current[permission][other_permission] = toggledValue;
            });
            break;
          default:
            current[permission] = toggledValue;
        }
      });
      if ("write" in current) {
        current["write"] = toggledValue;
      }
    }
    setPermissions(Object.assign({}, permissions, { [resource_key]: current }));
  };

  const handleSubmit = async () => {
    if (!id) {
      await createRole({
        name: name,
        permissions: JSON.stringify(permissions),
        isForExternalApprover: isForExternalApprover,
      }).then((res) => {
        const createdData = res?.data?.roleCreate;
        if (!createdData) {
          return;
        }
        if (createdData.errors.length > 0) {
          setOk(false);
          setAndOpenErrors(createdData.errors);
        } else {
          setOk(true);
          clearAndCloseErrors();
          // TODO: setTimeoutを消すこと(ダブルクリックの懸念あり)
          setTimeout(() => {
            if (createdData.role) {
              router.push(`/roles/${createdData.role.id}/edit`);
            }
          }, 1000);
        }
      });
    } else {
      await updateRole({
        id: id,
        name: name,
        permissions: JSON.stringify(permissions),
        isForExternalApprover: isForExternalApprover,
      }).then((res) => {
        const updatedData = res?.data?.roleUpdate;
        if (!updatedData) {
          return;
        }
        if (updatedData.errors.length > 0) {
          setOk(false);
          setAndOpenErrors(updatedData.errors);
        } else {
          setOk(true);
          clearAndCloseErrors();
        }
      });
    }
  };

  return (
    <NitoelPaper>
      <OkMsgs ok={ok} setOk={setOk} msgs={[t("common:saved")]} />
      <ErrMsgs errs={errs.map((err) => err.message)} ng={ng} setNg={setNg} />
      <Box>
        <Stack direction="row" alignItems="center" spacing={2}>
          <InputLabel label={t("role:shared:name")} required />
          <TextField size="small" value={name} onChange={(e) => setName(e.target.value)} />
        </Stack>
        {showExternalApproverCheckbox && (
          <>
            <FormControlLabel
              control={<Checkbox checked={isForExternalApprover} onChange={(e) => setIsForExternalApprover(e.target.checked)} />}
              label={t("role:shared.is_for_external_approver")}
            />
            {isForExternalApprover && (
              <Typography variant="caption" display="block">
                {t("role:shared.external_approver_permissions_fixed")}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box>
        <NitoelTable>
          <NitoelTHead>
            <NitoelTRow>
              <NitoelTHeaderCell></NitoelTHeaderCell>
              <NitoelTHeaderCell>read</NitoelTHeaderCell>
              <NitoelTHeaderCell>write</NitoelTHeaderCell>
              <NitoelTHeaderCell>export</NitoelTHeaderCell>
              <NitoelTHeaderCell>other</NitoelTHeaderCell>
            </NitoelTRow>
          </NitoelTHead>
          <NitoelTBody>
            {filteredResources.map((r, idx) => (
              <FormRow
                key={idx}
                resource={r}
                permissions={permissions}
                togglePermission={togglePermission}
                disabled={isForExternalApprover}
              />
            ))}
          </NitoelTBody>
        </NitoelTable>
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <PermittableButton onClick={() => loadingWrapper(handleSubmit)} resource="role" permission="write">
            {t("common:save")}
          </PermittableButton>
        </Stack>
      </Box>
    </NitoelPaper>
  );
};

export default RoleForm;
